/**
 * Open editors — several work-in-progress Add / Edit records at once.
 *
 * Pure state (a reducer the shell model drives) plus the sessionStorage
 * snapshot that lets the list survive a page reload. The FORM state itself is
 * never re-implemented here: each session's TxnScreen instance stays mounted
 * (kept alive) while minimised and holds its own values, child buffers, lookups
 * and item_change results. A snapshot is only the reload-recovery copy.
 *
 * Snapshot rules (see docs/plans/runtime-shell-layouts.md §5.3):
 *   - never secrets (secure / password columns are dropped by the screen before
 *     the snapshot reaches here) and never upload binaries (dropped here, listed
 *     in `omitted` so the restored form can say "re-attach");
 *   - sessionStorage only, keyed by application + user — never sent anywhere;
 *   - cleared on save, discard and sign-out; an old one is restored only on
 *     request, with a warning.
 */

export type EditorMode = 'add' | 'edit'

/** What a screen asked the shell to open. */
export interface EditorIntent {
  objName: string
  mode: EditorMode
  txnId?: string
  /** A worklist task's vetted seed (add). */
  seed?: Record<string, unknown>
  /** A handler='open' hand-off from another screen, applied once (`openKey`). */
  open?: unknown
  openKey?: string
}

export interface EditorSnapshot {
  header: Record<string, unknown>
  details: Record<string, Array<Record<string, unknown>>>
  /** The stored values the edit started from (the version source). */
  baseline?: Record<string, unknown> | null
  activeFormNo?: string
  /** Fields not kept (upload binaries) — the restored form asks to re-attach them. */
  omitted?: string[]
  savedAt: number
}

export interface EditorSession {
  id: string
  objName: string
  mode: EditorMode
  txnId?: string
  /** The record's title ("Dua Lipa", "New artist"). */
  title: string
  dirty: boolean
  openedAt: number
  /** The first-open request, consumed once by the session's screen instance. */
  intent?: EditorIntent
  /** True while its screen instance exists. False = rebuilt from a snapshot
   *  after a reload; mounted on first restore. */
  mounted: boolean
  /** Reload-recovery copy (present for sessions rebuilt after a reload). */
  snapshot?: EditorSnapshot
  /** The snapshot is older than the configured age: restore only on request, with a warning. */
  stale?: boolean
  /** Bumped on every restore — the instance re-acquires its lock and re-checks the record. */
  restoreNonce: number
}

export interface EditorsState {
  sessions: EditorSession[]
  /** The session shown (not minimised), or null. */
  activeId: string | null
}

export const EMPTY_EDITORS: EditorsState = { sessions: [], activeId: null }

let seq = 0
export function newSessionId(): string {
  seq += 1
  const rand = Math.random().toString(36).slice(2, 8)
  return `es_${Date.now().toString(36)}${seq.toString(36)}${rand}`
}

export type EditorOpenResult =
  | { kind: 'opened'; id: string }
  | { kind: 'focused'; id: string }
  | { kind: 'cap'; max: number }

/** The session already editing this record, if any (never open two). */
export function findEditSession(state: EditorsState, objName: string, txnId: string): EditorSession | null {
  return state.sessions.find((s) => s.mode === 'edit' && s.objName === objName
    && String(s.txnId) === String(txnId)) || null
}

/**
 * Open (or re-focus) a session. Edit on a record that already has a session
 * focuses it; at the cap nothing opens and the caller says so.
 */
export function openSession(
  state: EditorsState, intent: EditorIntent, opts: { max: number; title: string; now: number; id?: string },
): { state: EditorsState; result: EditorOpenResult } {
  if (intent.mode === 'edit' && intent.txnId) {
    const ex = findEditSession(state, intent.objName, intent.txnId)
    if (ex) {
      return {
        state: {
          sessions: state.sessions.map((s) => (s.id === ex.id
            ? { ...s, mounted: true, restoreNonce: s.restoreNonce + 1 } : s)),
          activeId: ex.id,
        },
        result: { kind: 'focused', id: ex.id },
      }
    }
  }
  if (state.sessions.length >= opts.max) return { state, result: { kind: 'cap', max: opts.max } }
  const id = opts.id || newSessionId()
  const session: EditorSession = {
    id, objName: intent.objName, mode: intent.mode, txnId: intent.txnId,
    title: opts.title, dirty: false, openedAt: opts.now, intent, mounted: true, restoreNonce: 0,
  }
  return { state: { sessions: [...state.sessions, session], activeId: id }, result: { kind: 'opened', id } }
}

export function minimiseSession(state: EditorsState, id?: string | null): EditorsState {
  const target = id ?? state.activeId
  if (!target || state.activeId !== target) return state
  return { ...state, activeId: null }
}

export function restoreSession(state: EditorsState, id: string): EditorsState {
  if (!state.sessions.some((s) => s.id === id)) return state
  return {
    sessions: state.sessions.map((s) => (s.id === id
      ? { ...s, mounted: true, restoreNonce: s.restoreNonce + 1 } : s)),
    activeId: id,
  }
}

export function updateSession(state: EditorsState, id: string, patch: Partial<EditorSession>): EditorsState {
  let changed = false
  const sessions = state.sessions.map((s) => {
    if (s.id !== id) return s
    const next = { ...s, ...patch }
    if ((Object.keys(patch) as (keyof EditorSession)[]).some((k) => s[k] !== next[k])) changed = true
    return next
  })
  return changed ? { ...state, sessions } : state
}

export function closeSession(state: EditorsState, id: string): EditorsState {
  if (!state.sessions.some((s) => s.id === id)) return state
  return {
    sessions: state.sessions.filter((s) => s.id !== id),
    activeId: state.activeId === id ? null : state.activeId,
  }
}

// ── Snapshots (sessionStorage) ────────────────────────────────────────────

export function snapshotKey(appKey: string, userKey: string): string {
  return `twasta:shell:editors:${appKey}:${userKey}`
}

interface StoredSession {
  id: string
  objName: string
  mode: EditorMode
  txnId?: string
  title: string
  dirty: boolean
  openedAt: number
  snapshot?: EditorSnapshot
}

function isBinary(v: unknown): boolean {
  if (v == null || typeof v !== 'object') {
    // An inlined file (data: URL) is a binary in disguise.
    return typeof v === 'string' && v.length > 1024 && /^data:[^,]*;base64,/.test(v)
  }
  if (typeof Blob !== 'undefined' && v instanceof Blob) return true
  if (typeof ArrayBuffer !== 'undefined' && (v instanceof ArrayBuffer || ArrayBuffer.isView(v))) return true
  return false
}

/** True for a value that is, or carries, an upload binary. */
function carriesBinary(v: unknown, depth = 0): boolean {
  if (isBinary(v)) return true
  if (depth > 3 || v == null || typeof v !== 'object') return false
  if (Array.isArray(v)) return v.some((x) => carriesBinary(x, depth + 1))
  return Object.values(v as Record<string, unknown>).some((x) => carriesBinary(x, depth + 1))
}

/** Plain JSON-safe copy of one record's values, dropping binaries (named in `omitted`)
 *  and the keys the screen marked secret. */
export function sanitizeValues(
  values: Record<string, unknown>, secretKeys: Set<string>, omitted: string[], prefix = '',
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(values || {})) {
    if (secretKeys.has(k.toLowerCase())) continue
    if (k === '__children' && v && typeof v === 'object') {
      out[k] = sanitizeDetails(v as Record<string, Array<Record<string, unknown>>>, secretKeys, omitted, `${prefix}${k}.`)
      continue
    }
    if (carriesBinary(v)) { omitted.push(`${prefix}${k}`); continue }
    if (typeof v === 'function' || typeof v === 'symbol') continue
    out[k] = v
  }
  return out
}

export function sanitizeDetails(
  details: Record<string, Array<Record<string, unknown>>>, secretKeys: Set<string>, omitted: string[], prefix = '',
): Record<string, Array<Record<string, unknown>>> {
  const out: Record<string, Array<Record<string, unknown>>> = {}
  for (const [formNo, rows] of Object.entries(details || {})) {
    if (!Array.isArray(rows)) continue
    out[formNo] = rows.map((r, i) => sanitizeValues(r || {}, secretKeys, omitted, `${prefix}${formNo}[${i}].`))
  }
  return out
}

/** Build a snapshot from what a screen holds. */
export function makeSnapshot(input: {
  header: Record<string, unknown>
  details: Record<string, Array<Record<string, unknown>>>
  baseline?: Record<string, unknown> | null
  activeFormNo?: string
  secretKeys: Iterable<string>
  now: number
}): EditorSnapshot {
  const secrets = new Set([...input.secretKeys].map((k) => k.toLowerCase()))
  const omitted: string[] = []
  const header = sanitizeValues(input.header, secrets, omitted)
  const details = sanitizeDetails(input.details, secrets, omitted)
  const baseline = input.baseline ? sanitizeValues(input.baseline, secrets, []) : input.baseline ?? null
  // Round-trip through JSON so nothing un-serialisable (a Date is fine — it
  // becomes its ISO text, which is how the form posts it anyway) reaches storage.
  const safe = JSON.parse(JSON.stringify({ header, details, baseline })) as {
    header: Record<string, unknown>; details: EditorSnapshot['details']; baseline: Record<string, unknown> | null
  }
  return { ...safe, activeFormNo: input.activeFormNo, omitted: omitted.length ? omitted : undefined, savedAt: input.now }
}

export function writeSnapshots(appKey: string, userKey: string, sessions: EditorSession[]): void {
  if (!appKey || !userKey) return
  try {
    const stored: StoredSession[] = sessions.map((s) => ({
      id: s.id, objName: s.objName, mode: s.mode, txnId: s.txnId, title: s.title,
      dirty: s.dirty, openedAt: s.openedAt, snapshot: s.snapshot,
    }))
    if (!stored.length) sessionStorage.removeItem(snapshotKey(appKey, userKey))
    else sessionStorage.setItem(snapshotKey(appKey, userKey), JSON.stringify({ v: 1, sessions: stored }))
  } catch {
    /* storage refused (quota, private mode): the kept-alive forms still hold the work */
  }
}

/** Sessions rebuilt from the snapshot store, minimised and unmounted. */
export function readSnapshots(
  appKey: string, userKey: string, opts: { now: number; maxAgeHours: number; max: number },
): EditorSession[] {
  if (!appKey || !userKey) return []
  try {
    const raw = sessionStorage.getItem(snapshotKey(appKey, userKey))
    if (!raw) return []
    const doc = JSON.parse(raw) as { sessions?: StoredSession[] }
    const maxAgeMs = opts.maxAgeHours * 3600 * 1000
    return (Array.isArray(doc.sessions) ? doc.sessions : [])
      .filter((s) => s && typeof s.id === 'string' && typeof s.objName === 'string'
        && (s.mode === 'add' || s.mode === 'edit'))
      .slice(0, opts.max)
      .map((s) => {
        const at = s.snapshot?.savedAt || s.openedAt || 0
        return {
          id: s.id, objName: s.objName, mode: s.mode, txnId: s.txnId,
          title: String(s.title || ''), dirty: !!s.dirty, openedAt: Number(s.openedAt) || at,
          snapshot: s.snapshot, mounted: false, restoreNonce: 0,
          stale: !!at && opts.now - at > maxAgeMs,
        }
      })
  } catch {
    return []
  }
}

export function clearSnapshots(appKey: string, userKey: string): void {
  try { sessionStorage.removeItem(snapshotKey(appKey, userKey)) } catch { /* nothing to clear */ }
}

/**
 * The compare-and-set text rule — the twin of the engine's
 * `executor._expect_text` (Java `Executor.expectText`), so the restore-time
 * check and the save-time `expect` agree on what "unchanged" means.
 */
export function expectText(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (value instanceof Date) return value.toISOString()
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return String(value)
    return Number.isInteger(value) ? String(value) : numRepr(value)
  }
  const t = String(value).trim()
  if (t && !/^(nan|[+-]?inf(inity)?)$/i.test(t) && /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/.test(t)) {
    const f = Number(t)
    if (Number.isFinite(f)) return Number.isInteger(f) ? String(f) : numRepr(f)
  }
  return t
}

function numRepr(f: number): string {
  // Python's repr(float) is the shortest round-trip form, as is JS String().
  return String(f)
}

/** The baseline fields whose stored value is no longer what the edit started from. */
export function changedSince(
  baseline: Record<string, unknown> | null | undefined, stored: Record<string, unknown>,
): string[] {
  if (!baseline) return []
  const lower = new Map(Object.entries(stored || {}).map(([k, v]) => [k.toLowerCase(), v]))
  return Object.entries(baseline)
    .filter(([k, v]) => expectText(v) !== expectText(lower.get(k.toLowerCase())))
    .map(([k]) => k)
}
